import c from "./Sidebar.module.scss";
import ItemCustomization from "./ItemCustomization";
import "overlayscrollbars/overlayscrollbars.css";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { useEffect, useMemo, useState } from "react";
import usePlanCopyConf from "../../hooks/plan-copy-conf.hook";
import {
	getCopier,
	getOrganizationPlan,
	getOrganizations,
} from "../../services/organizations.service";
import Tabs from "../tabs/Tabs";
import { cn } from "../../lib/utils";
import Filter from "../utils/filter/Filter";
import { infiniteScrollBottomHandler } from "../../helper/common.helper";
import debounce from "lodash.debounce";
import LoadingBox from "../loading/LoadingBox";
import CloneContentModal from "../popups/plan-copy/CloneContentPopup";

function Sidebar({ configurations, disableCreate }) {
	const {
		setCrudConfOpen,
		selectedConfId,
		updateSelectedConf,
		setDeleteModalOpen,
		setDeleteConf,
		setDeleteItem,
		deleteConfigurationHandler,
		setCrudModel,
		planData,
		mode,
		setMode,
		setSelectedOrganization,
		selectedOrganization,
		defaultOrganization,
		setPlanChapters,
		setLoadingCopyPlan,
		loadingCopyPlan,
	} = usePlanCopyConf();

	const [loadingEdit, setLoadingEdit] = useState(false);

	const [searchOrganization, setSearchOrganization] = useState("");

	const [cloneConfigurationModel, setCloneConfigurationModel] = useState({
		isOpen: false,
		item: null,
	});

	const [isOrganizationLoading, setIsOrganizationLoading] = useState({
		init: false,
		more: false,
	});

	const [organizations, setOrganizations] = useState([]);

	const [metaListOrganization, setMetaListOrganization] = useState({
		current_page: 1,
		last_page: 1,
	});

	const filterOrganizations = useMemo(
		() => organizations.filter((item) => item.id !== defaultOrganization?.organization?.id),
		[JSON.stringify(organizations)]
	);

	const getListOrganizations = async (page) => {
		if (!isOrganizationLoading.init && !isOrganizationLoading.more) {
			const body = {
				template_plan_id: planData.template_plan_id,
				include: ["organizationPlans"],
				filter: {
					name: searchOrganization.trim(),
					template_plan_active: true,
				},
				page: page,
				per_page: 15,
			};

			await getOrganizations(
				body,
				async (res) => {
					setIsOrganizationLoading({ init: false, more: false });
					setMetaListOrganization(res.meta);
					if (page > 1) {
						setOrganizations((items) => [...items, ...res.data]);
					} else {
						setOrganizations((items) => res.data);
					}
				},
				() => {
					setIsOrganizationLoading({ init: false, more: false });
				}
			);
		}
	};

	useEffect(() => {
		if (mode === "import") {
			setIsOrganizationLoading((l) => ({ ...l, init: true }));
			getListOrganizations(1);
		}
	}, [searchOrganization]);

	/**
	 * Delete item request
	 * @param {*} item
	 */
	const deleteItem = (item) => {
		setDeleteItem(item);
		setDeleteConf({
			title: "Elimina configurazione",
			icon: "icon_16 default folder",
			subtitle: item.name,
			confirmHandler: deleteConfigurationHandler,
		});
		setDeleteModalOpen(true);
	};

	/**
	 * Edit item request
	 * @param {*} item
	 */
	const editItem = async (item) => {
		if (!loadingEdit) {
			setLoadingEdit(true);
			const copier = await getCopier({
				id: item.id,
				idCorp: selectedOrganization?.organization?.id,
			});
			setLoadingEdit(false);
			setCrudModel(copier.data);
			setCrudConfOpen(true);
		}
	};

	/**
	 * new item request
	 * @param {*} item
	 */
	const newItem = () => {
		setCrudModel(null);
		setCrudConfOpen(true);
	};

	const debouncedSearch = debounce(setSearchOrganization, 300);

	const infiniteScrollOrganization = (e) => {
		if (metaListOrganization.current_page !== metaListOrganization.last_page) {
			infiniteScrollBottomHandler(e, () => {
				setIsOrganizationLoading((l) => ({ ...l, more: true }));
				getListOrganizations(metaListOrganization.current_page + 1);
			});
		}
	};

	const getPlanInfo = async (idCorp, idPlan) => {
		setLoadingCopyPlan((l) => ({ ...l, planInfo: true }));
		const body = {
			id: idCorp,
			idPlan: idPlan,
			include: [
				"organization",
				"templatePlan",
				"organizationChapters",
				"organizationChapterContentsCount",
				"organizationGoalsCount",
				"organizationRequirementsCount",
				"organizationChapters.templateChapter",
				"organizationChapters.organizationChapterContents",
				"organizationChapters.organizationChapterContents.templateChapterContent",
				"organizationChapters.organizationGoals",
				"organizationChapters.organizationGoals.templateGoal",
				"organizationChapters.organizationGoals.organizationRequirements",
				"organizationChapters.organizationGoals.organizationRequirements.templateRequirement",
			],
		};

		const plan = await getOrganizationPlan(body);

		setLoadingCopyPlan((l) => ({ ...l, planInfo: false }));

		return plan?.data;
	};

	const onSwitchHandler = (value) => {
		if (value !== mode) {
			setMode(value);
			setSearchOrganization("");
			updateSelectedConf(null);

			if (value === "import") {
				setIsOrganizationLoading((l) => ({ ...l, init: true }));
				getListOrganizations(1);
				setSelectedOrganization({ organization: null, plan: null });
			} else {
				setPlanChapters(defaultOrganization.plan.organization_contents);
				setSelectedOrganization(defaultOrganization);
			}
		}
	};

	const onSelectOrganization = async (organization) => {
		const findPlan = organization.organization_plans.find(
			(p) => p.template_plan_id === planData.template_plan_id
		);
		const plan = await getPlanInfo(organization.id, findPlan?.id ?? 0);

		setPlanChapters(plan.organization_contents);

		setSelectedOrganization({
			organization: organization,
			plan: plan ?? null,
		});
	};

	return (
		<div className={c.main_sidebar}>
			<div className={c.wrap_sidebar}>
				{/* Header Sidebar */}
				<div className={c.main_header_sidebar}>
					<div className={c.main_title}>
						<div className={c.title}>Modalità dell’operazione</div>
					</div>

					<div className="pb-7">
						<Tabs
							selected={mode}
							onChange={onSwitchHandler}
							options={[
								{ label: "Esportazione", value: "export" },
								{ label: "Importazione", value: "import" },
							]}
						/>
					</div>

					{mode === "import" && (
						<div className="mb-7">
							<Filter
								className={{ button: "w-full", popover: "w-full" }}
								customToggle={({ isOpen }) => (
									<div
										className={cn(
											"flex justify-between text-left items-center font-bold text-dark-70 rounded-[4px] p-[9px_14px_8px] border border-dark-30 w-full gap-[12px]",
											isOpen && "bg-interactive-20 outline-interactive-100 outline"
										)}
									>
										<span className="w-0 flex-1 truncate">
											{selectedOrganization?.organization?.name ||
												"Seleziona l’ente da cui importare contenuti"}
										</span>
										<span
											className={cn(
												"flex-none flex icon_16 default chevron icon_mask !bg-interactive-50",
												isOpen && "top"
											)}
										></span>
									</div>
								)}
							>
								<Filter.List
									confirmLabel="Applica"
									options={filterOrganizations.map((organization) => ({
										id: organization.id,
										label: organization.name,
										value: organization,
										icon: organization?.logo_url ? (
											<img
												src={organization.logo_url}
												className="object-contain size-[16px] mt-[3px]"
												alt=""
											/>
										) : (
											<span className="icon_16 default entity mt-[3px] opacity-40" />
										),
									}))}
									activeOptions={[selectedOrganization.organization]}
									onSearch={{
										value: searchOrganization,
										handler: debouncedSearch,
									}}
									isLoadingMore={isOrganizationLoading.more}
									onScroll={infiniteScrollOrganization}
									type="radio"
									onApply={onSelectOrganization}
								/>
							</Filter>
						</div>
					)}

					{!!selectedOrganization.organization && (
						<div className="fade_in">
							<div className={c.main_title}>
								<div className={c.title}>Seleziona il contenuto da copiare</div>
							</div>

							<div className={c.main_item}>
								<ItemCustomization
									onChange={() => updateSelectedConf(null)}
									checked={selectedConfId == null}
									id="copy_all_plan"
									name="conf_choice"
									label="Tutto il contenuto"
								/>
							</div>
						</div>
					)}
				</div>

				{!!selectedOrganization.organization && (
					<div className={cn(c.main_content_sidebar, "fade_in")}>
						<div className={c.main_heading}>
							<div className={c.main_title}>
								<div className={c.title}>Configurazioni di copia</div>
							</div>

							<div className={c.main_action}>
								<button
									type="button"
									onClick={() => !disableCreate && newItem()}
									className={
										"link-interactive flex items-center gap-2" +
										(disableCreate ? " link_disabled" : "")
									}
									{...(disableCreate && {
										"data-tooltip-id": "tooltip_small_top",
										"data-tooltip-offset": 10,
										"data-tooltip-content": "Nessun contenuto da copiare",
									})}
								>
									<span className="icon_16 plus default icon_mask !bg-interactive-100 -mt-1"></span>
									Crea
								</button>
							</div>
						</div>

						<OverlayScrollbarsComponent className={c.main_list_item}>
							<div className={c.wrap_list_item}>
								{loadingCopyPlan.copiers ? (
									<LoadingBox blocked="true" />
								) : (
									configurations.map((conf, i) => (
										<ItemCustomization
											key={i}
											onChange={() => updateSelectedConf(conf.id)}
											checked={String(selectedConfId) === String(conf.id)}
											id={`copy_item_${conf.id}`}
											name="conf_choice"
											label={conf.name}
											onDuplicate={() => setCloneConfigurationModel({ isOpen: true, item: conf })}
											onEdit={() => editItem(conf)}
											onDelete={() => deleteItem(conf)}
										/>
									))
								)}
							</div>
						</OverlayScrollbarsComponent>
					</div>
				)}
			</div>
			<CloneContentModal
				open={cloneConfigurationModel.isOpen}
				model={cloneConfigurationModel.item}
				setOpen={() => setCloneConfigurationModel((old) => ({ ...old, isOpen: false }))}
			/>
		</div>
	);
}

export default Sidebar;
